import { CatalogFolder, catalogNameFromPath } from './catalogPaths';

const SETTINGS_PATH = '/settings.json';

export type CatalogFileEntry = {
  id: string;
  path: string;
  label?: string;
  hardwareRef?: string;
};

export type CatalogSyncResult = {
  changed: boolean;
  missingOnDevice?: boolean;
};

type SettingsBag = Record<string, unknown>;

function asObject(value: unknown): SettingsBag {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as SettingsBag
    : {};
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value as T[] : [];
}

export function parseSettingsContent(content: string): SettingsBag {
  try {
    const parsed = JSON.parse(String(content || '{}'));
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as SettingsBag
      : {};
  } catch {
    return {};
  }
}

export function serializeSettingsContent(settings: SettingsBag): string {
  return `${JSON.stringify(settings, null, 2)}\n`;
}

function catalogFolderFromPath(path: string): CatalogFolder | null {
  if (path.startsWith('/layouts/')) return 'layouts';
  if (path.startsWith('/signals/')) return 'signals';
  if (path.startsWith('/flows/')) return 'flows';
  return null;
}

function listEntries(settings: SettingsBag, folder: CatalogFolder): CatalogFileEntry[] {
  if (folder === 'layouts') {
    const display = asObject(settings.display);
    return asArray<CatalogFileEntry>(display.layouts);
  }
  if (folder === 'signals') {
    const signals = asObject(settings.signals);
    return asArray<CatalogFileEntry>(signals.files);
  }
  const flows = asObject(settings.flows);
  return asArray<CatalogFileEntry>(flows.files);
}

function writeEntries(settings: SettingsBag, folder: CatalogFolder, entries: CatalogFileEntry[]): SettingsBag {
  if (folder === 'layouts') {
    const display = { ...asObject(settings.display), layouts: entries };
    return { ...settings, display };
  }
  if (folder === 'signals') {
    const signals = { ...asObject(settings.signals), files: entries };
    return { ...settings, signals };
  }
  const flows = { ...asObject(settings.flows), files: entries };
  return { ...settings, flows };
}

export function ensureCatalogEntry(
  settings: SettingsBag,
  path: string,
  options?: { label?: string; hardwareRef?: string; setActive?: boolean },
): { settings: SettingsBag; result: CatalogSyncResult } {
  const folder = catalogFolderFromPath(path);
  if (!folder || folder === 'flows' || folder === 'layouts') {
    return { settings, result: { changed: false } };
  }
  const id = catalogNameFromPath(path, folder);
  if (!id) {
    return { settings, result: { changed: false } };
  }

  const entries = listEntries(settings, folder);
  const existing = entries.find((entry) => entry.id === id || entry.path === path);
  if (existing) {
    let changed = false;
    const nextEntry = { ...existing };
    if (options?.label && !existing.label) {
      nextEntry.label = options.label;
      changed = true;
    }
    if (options?.hardwareRef && !existing.hardwareRef) {
      nextEntry.hardwareRef = options.hardwareRef;
      changed = true;
    }
    if (!changed) {
      return { settings, result: { changed: false } };
    }
    const nextEntries = entries.map((entry) => (
      entry.id === existing.id || entry.path === existing.path ? nextEntry : entry
    ));
    let nextSettings = writeEntries(settings, folder, nextEntries);
    if (options?.setActive) {
      nextSettings = setActiveCatalog(nextSettings, folder, id);
    }
    return { settings: nextSettings, result: { changed: true } };
  }

  const entry: CatalogFileEntry = {
    id,
    path,
    label: options?.label || id,
    hardwareRef: options?.hardwareRef,
  };
  let nextSettings = writeEntries(settings, folder, [...entries, entry]);
  if (options?.setActive) {
    nextSettings = setActiveCatalog(nextSettings, folder, id);
  }
  return { settings: nextSettings, result: { changed: true, missingOnDevice: true } };
}

function setActiveCatalog(settings: SettingsBag, folder: CatalogFolder, id: string): SettingsBag {
  if (folder === 'layouts') {
    const display = { ...asObject(settings.display), activeLayout: id };
    return { ...settings, display };
  }
  if (folder === 'flows') {
    const flows = { ...asObject(settings.flows), active: id };
    return { ...settings, flows };
  }
  return settings;
}

export { SETTINGS_PATH };
